import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import { useRetainedState, useTenantSettings } from "@appretio/shared";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import type { CubeWindow } from "../../components/cubeDataTable/utils/columnUtils";
import {
  defaultWeekLabelMode,
  getDefaultPlanningYear,
  getPlanningYearOptions
} from "../../components/tableControls/forecastTableControlUtils";
import {
  DEFAULT_FORECAST_TABLE_CONTROL_OPTIONS,
  DEFAULT_FORECAST_TABLE_CONTROL_VALUES,
  type ForecastTableControlOptions,
  type ForecastTableControlValues
} from "../../components/tableControls/forecastTableControlValues";
import { type CubePage, pageScope } from "../useCubeSelectionState";
import { useFetchForecastMetaData } from "../useFetchForecastMetaData";

/** Context + cube selection captured by the most recent Apply click. */
export interface AppliedCubeFilters {
  /** Context filters at Apply time. */
  context: ForecastContextSelectedOptions;
  /** Flat cube-id list at Apply time. */
  cubeIds: string[];
  /**
   * The writable own cube at Apply time, its week window and whether it is
   * frozen. The grid shows this cube's data until the next Apply, so the edit
   * gate, the recompute's window and the save follow it rather than the
   * picker's current choice.
   */
  saveCubeId?: string;
  saveCubeWindow?: CubeWindow;
  isFrozen?: boolean;
}

/** What a toast shows. */
export interface ToastState {
  open: boolean;
  title: string;
  message: string;
  /** `default` is a neutral notice — a write still in flight; `success` an outcome. */
  variant: "default" | "success";
  /** `Infinity` keeps the toast until the user closes it; unset, the Toast's own timer. */
  duration?: number;
}

/** UI chrome state for the forecast workspace: controls, toast, dialog flags. */
export interface WorkspaceTableState {
  /** Server-reported timestamp of the last successful save. */
  savedAt: string | null;
  /** Toast notification state. Each action that surfaces a success Toast
   * sets its own title and message so the user sees feedback scoped to
   * the action they triggered (Save vs Restate, etc.) rather than a
   * generic header. */
  toastState: ToastState & {
    /** Bumped each time a toast is raised, so the page can remount it. */
    raised: number;
  };
  /** Whether the user clicked Apply with no cube id selected. */
  hasVersionError: boolean;
  /** Filters used by the most recent Apply click. */
  appliedFilters: AppliedCubeFilters;
  /** Whether the restate-FPOS confirmation dialog is open. */
  restateFPOSDialogOpen: boolean;
  /** Table control option definitions (dropdowns/toggles available). */
  tableControlOptions: ForecastTableControlOptions;
  /** Current table control values (calendar view, intake mode, etc.). */
  tableControls: ForecastTableControlValues;
  /** Set the saved-at timestamp. */
  setSavedAt: Dispatch<SetStateAction<string | null>>;
  /** Set the toast state. */
  setToastState: Dispatch<SetStateAction<ToastState>>;
  /** Convenience setter for just the toast open flag. */
  setToastOpen: (open: boolean) => void;
  /** Set the version error flag. */
  setHasVersionError: Dispatch<SetStateAction<boolean>>;
  /** Set the applied filters. */
  setAppliedFilters: Dispatch<SetStateAction<AppliedCubeFilters>>;
  /** Set the restate-FPOS dialog open state. */
  setRestateFPOSDialogOpen: (open: boolean) => void;
  /** Set the table control option definitions. */
  setTableControlOptions: Dispatch<SetStateAction<ForecastTableControlOptions>>;
  /** Set the current table control values. */
  setTableControls: Dispatch<SetStateAction<ForecastTableControlValues>>;
}

/**
 * Manages all UI chrome state for the forecast workspace: table controls,
 * toast notifications, dialog flags, applied filters, and version error.
 *
 * @param page The page whose control bar this is: the controls, applied
 *   filters and save timestamp are retained per page, so they survive
 *   navigating away and back (and reset on reload).
 */
export function useWorkspaceTableState(page: CubePage): WorkspaceTableState {
  const retainScope = pageScope(page);
  // Retained with the grid data they describe (``useRetainedForecastData``),
  // so the dirty check and the save indicator match the grid on screen.
  const [savedAt, setSavedAt] = useRetainedState<string | null>(
    retainScope,
    "savedAt",
    null
  );
  const [toastState, setToast] = useState<WorkspaceTableState["toastState"]>({
    open: false,
    title: "",
    message: "",
    variant: "default",
    raised: 0
  });
  // Raised over an open toast, the new one keeps the old dismiss timer unless
  // it remounts; the page keys the toast on `raised`.
  const setToastState = useCallback((update: SetStateAction<ToastState>) => {
    setToast((prev) => {
      const next = typeof update === "function" ? update(prev) : update;
      return { ...next, raised: next.open ? prev.raised + 1 : prev.raised };
    });
  }, []);
  const [hasVersionError, setHasVersionError] = useState(false);
  const [appliedFilters, setAppliedFilters] =
    useRetainedState<AppliedCubeFilters>(retainScope, "appliedFilters", {
      context: {},
      cubeIds: []
    });
  const [restateFPOSDialogOpen, setRestateFPOSDialogOpen] = useState(false);

  const [tableControlOptions, setTableControlOptions] =
    useRetainedState<ForecastTableControlOptions>(
      retainScope,
      "tableControlOptions",
      DEFAULT_FORECAST_TABLE_CONTROL_OPTIONS
    );
  const weekLabelBasis =
    useTenantSettings().data?.settings.week_label_basis.value;
  const [tableControls, setTableControls] =
    useRetainedState<ForecastTableControlValues>(retainScope, "tableControls", {
      ...DEFAULT_FORECAST_TABLE_CONTROL_VALUES,
      weekLabels: defaultWeekLabelMode(weekLabelBasis)
    });

  // The settings can resolve after mount, so upgrade the initial "settings
  // loading" default once, when the basis first lands. The seed-once ref plus
  // the still-default check keep the effect from overwriting an explicit
  // toggle choice — whether made before or after the settings resolved — and
  // from firing again on a later settings refetch.
  const weekLabelsSeededRef = useRef(weekLabelBasis !== undefined);
  useEffect(() => {
    if (weekLabelsSeededRef.current || weekLabelBasis === undefined) return;
    weekLabelsSeededRef.current = true;
    setTableControls((prev) => {
      const seeded = defaultWeekLabelMode(weekLabelBasis);
      return prev.weekLabels === defaultWeekLabelMode(undefined) &&
        prev.weekLabels !== seeded
        ? { ...prev, weekLabels: seeded }
        : prev;
    });
  }, [weekLabelBasis, setTableControls]);

  // The fiscal planning-year options are empty until the metadata lands — a
  // clock cannot name the tenant's fiscal year. Seed from the real mapping
  // once, same guard shape as weekLabels above.
  const { data: metaData } = useFetchForecastMetaData();
  const { calendarView } = tableControls;
  const planningYearSeededRef = useRef(false);
  useEffect(() => {
    if (planningYearSeededRef.current || !metaData) return;
    planningYearSeededRef.current = true;
    const options = getPlanningYearOptions(calendarView, metaData);
    setTableControlOptions((prev) => ({ ...prev, planningYear: options }));
    setTableControls((prev) =>
      prev.planningYear === DEFAULT_FORECAST_TABLE_CONTROL_VALUES.planningYear
        ? {
            ...prev,
            planningYear: getDefaultPlanningYear(calendarView, metaData)
          }
        : prev
    );
  }, [metaData, calendarView, setTableControlOptions, setTableControls]);

  const setToastOpen = useCallback((open: boolean) => {
    setToast((prev) => ({ ...prev, open }));
  }, []);

  return {
    savedAt,
    toastState,
    hasVersionError,
    appliedFilters,
    restateFPOSDialogOpen,
    tableControlOptions,
    tableControls,
    setSavedAt,
    setToastState,
    setToastOpen,
    setHasVersionError,
    setAppliedFilters,
    setRestateFPOSDialogOpen,
    setTableControlOptions,
    setTableControls
  };
}
